import React from 'react';
import {
  Box,
  Select,
  MenuItem,
  Typography,
  CircularProgress,
  Chip,
  FormControl,
  InputLabel,
} from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';

export interface TradeSelectorItem {
  id: number;
  tradeOid: string;
  name: string;
}

interface MappingTradeSelectorProps {
  trades: TradeSelectorItem[];
  selectedTradeId: number | null;
  questionCount: number;
  taskCount: number;
  loading: boolean;
  onChange: (tradeId: number, tradeOid: string) => void;
}

export function MappingTradeSelector({
  trades,
  selectedTradeId,
  questionCount,
  taskCount,
  loading,
  onChange,
}: MappingTradeSelectorProps) {
  const handleChange = (e: SelectChangeEvent<number>) => {
    const id = e.target.value as number;
    const trade = trades.find(t => t.id === id);
    if (trade) onChange(trade.id, trade.tradeOid);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        px: 2.5,
        py: 1.25,
        borderBottom: '1px solid',
        borderColor: 'divider',
        background: '#fafafa',
        flexShrink: 0,
      }}
    >
      <FormControl size="small" sx={{ minWidth: 220 }}>
        <InputLabel id="mapping-trade-label">Trade</InputLabel>
        <Select<number>
          labelId="mapping-trade-label"
          label="Trade"
          value={selectedTradeId ?? ''}
          onChange={handleChange}
          disabled={loading}
          displayEmpty
        >
          {trades.map(t => (
            <MenuItem key={t.id} value={t.id}>
              {t.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {loading && <CircularProgress size={18} thickness={4} />}

      {!loading && selectedTradeId !== null && (questionCount > 0 || taskCount > 0) && (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Chip
            label={`${questionCount} question${questionCount !== 1 ? 's' : ''}`}
            size="small"
            variant="outlined"
            sx={{ fontSize: 11 }}
          />
          <Chip
            label={`${taskCount} task${taskCount !== 1 ? 's' : ''}`}
            size="small"
            variant="outlined"
            sx={{ fontSize: 11 }}
          />
        </Box>
      )}

      {!loading && selectedTradeId === null && (
        <Typography variant="caption" color="text.secondary">
          Select a trade to load its questions and tasks
        </Typography>
      )}
    </Box>
  );
}
